import React from 'react';
import { INotesPageView } from "./type/my-notes-page.types";
import notes from "../../resources/notes";
import { Button } from "antd";
import {
    CalendarClock,
    Check,
    CircleAlert,
    ClipboardCheck,
    NotebookPen,
    Plus,
    SquareCheckBig,
    SquarePen,
    Trash2,
    TriangleAlert
} from "lucide-react";
import Toolbar from "./utils/toolbar";
import AddNoteModal from "./utils/add-note-modal";
import { Checkbox, Tooltip } from "antd/lib";
import { Note, Priority } from "../../domains/models/note";
import dayjs from 'dayjs';
import "../../styles/component/my-notes-page.scss";


const MyNotesPageView: React.FC<INotesPageView> = (props) => {

    const resources = notes;

    const cards = [
        {
            title: resources.totalNotesLabel,
            key: 'total',
            icon: <SquareCheckBig/>,
            value: props.noteCount.total
        },
        {
            title: resources.completeNotesLabel,
            key: 'completed',
            icon: <ClipboardCheck/>,
            value: props.noteCount.completed
        },
        {
            title: resources.overdueNotesLabel,
            key: 'overdue',
            icon: <TriangleAlert/>,
            value: props.noteCount.overdue
        }
    ];


    const renderDate = (date?: string) => (date ? dayjs(date).format('DD MMM YYYY') : '')

    const isCompleted = (note: Note) => {
        return note.status === 'COMPLETED';
    }

    function handleMarkComplete(item: Note) {
        props.handleNoteMarkCompleted(item.id);
    }

    const renderPriorityItem = (val: keyof typeof Priority) => {
        if (val === 'HIGH') {
            return <span className={'priority high'}>
                <TriangleAlert className={'alert-icon '}/>
                {Priority[val]}
            </span>
        } else if (val === 'MEDIUM') {
            return <span className={'priority medium'}>
                <CircleAlert className={'medium-alert-icon '}/>
                {Priority[val]}
            </span>
        } else if (val === 'LOW') {
            return <span className={'priority low'}>
                <Check className={'check-icon '}/>
                {Priority[val]}
            </span>
        } else {
            return;
        }
    }

    return (
        <div className={'hrms-notes-page'}>
            <section className={'hrms-notes__header'}>

                <h2 className={'title'}>{resources.title}</h2>
                <Button
                    type={'primary'}
                    icon={<Plus/>}
                    className={'add-item-btn'}
                    onClick={props.openAddNoteModal}
                >
                    {resources.addNewNoteButtonText}
                </Button>

            </section>

            <section className={'hrms-notes__cards'}>
                {cards.map((item) => (
                    <div className={`card-item ${item.key}`} key={item.key}>
                        <div className={'icon-wrapper'}>
                            {item.icon}
                        </div>
                        <div className={'card-content'}>
                            <h3 className={'value'}>{item.value}</h3>
                            <span className={'label'}>{item.title}</span>
                        </div>
                    </div>
                ))}
            </section>

            <Toolbar
                sortOptionSelected={props.sortOptionSelected}
                handleSortChange={props.handleSortChange}
                loadNotes={props.loadNotes}
            />

            <section className={'hrms-notes__list'}>
                {props.notesList.length > 0 ? (props.notesList.map((item) => (
                        <div
                            className={`note-item 
                            ${isCompleted(item) ? 'completed' : ''} 
                            ${item.colorKey ? item.colorKey : 'default'}`}
                            key={item.id}
                        >
                            <Tooltip
                                title={!isCompleted(item) ? "mark note as complete" : "mark note as undone"}
                                classNames={{root:'mark-note-complete-tooltip'}}
                            >
                                <Checkbox
                                    className={'note-item-mark-done-btn'}
                                    checked={isCompleted(item)}
                                    onChange={() => handleMarkComplete(item)}
                                    id={item.id}
                                />
                            </Tooltip>

                            <div className={'note-item-header'}>
                                <h3 className={'title'}>{item.title}</h3>
                                <div className={'actions'}>
                            <span className={'icon-wrapper pin'}
                                  onClick={() => props.handleNotesPinned(item.id)}
                            >
                                <img src={'images/push-pin.png'} alt={'pin'}
                                     className={'pin-image'}/></span>
                                    <span
                                        className={'icon-wrapper edit'}
                                        onClick={() => props.openEditNoteModal(item)}
                                    >
                                <SquarePen className={'action-icon'}/>
                            </span>
                                    <span
                                        className={'icon-wrapper delete'}
                                        onClick={() => props.handleNoteDelete(item.id)}
                                    >
                                <Trash2 className={'action-icon'}/>
                            </span>
                                </div>
                            </div>
                            <div
                                className={'note-item-content'}
                                dangerouslySetInnerHTML={{__html: item.content}}
                            />

                            <div className={'note-item-time-priority'}>
                                {item.priority &&
                                renderPriorityItem(item.priority)
                                }
                                {item.dueDate && <span className={'due-date'}>
                                    <CalendarClock className={'due-date-icon'}/>
                                    {renderDate(item.dueDate)}</span>
                                }
                                {item.pinned && <span className={'pin-info'}>
                                   <img src={'images/push-pin.png'} alt={'pin'}
                                        className={'pin-image'}/>
                                        Pinned
                                </span>
                                }
                            </div>

                            <div className={'note-item-footer'}>
                                <div className={'tags-wrapper'}>
                                    {item.tag?.map((t) => (
                                        <span className={'tag'} key={t}>
                                               #{t}
                                        </span>
                                    ))}
                                </div>
                                <span className={'date'}>
                                    {renderDate(item.auditInfo?.createdOn)}
                                </span>
                            </div>

                        </div>
                    )))
                    :
                    (
                        <div className={'empty-content-wrapper'}>
                            <h2 className={'empty-title'}>{resources.emptyTitle}</h2>
                            <NotebookPen className={'empty-icon'}/>
                            <p className={'empty-description'}>{resources.emptyContent}
                            </p>
                        </div>
                    )
                }
            </section>

            <AddNoteModal
                modalState={props.modalState}
                closeAddNoteModal={props.closeAddNoteModal}
                upsertNotes={props.upsertNotes}
            />

        </div>
    )
}


export default MyNotesPageView;
